"use client";

import Image from "next/image";
import { ArrowUpRight, CalendarDays, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useLocale } from "@/lib/locale/locale-provider";
import type { ScheduleItem } from "@/lib/sections/schedule.types";
import { getMapsUrl } from "@/lib/maps";
import { formatDate } from "@/lib/date";
import styles from "./schedule.module.css";

type ScheduleEventCardProps = {
  item: ScheduleItem;
  index: number;
  align: "left" | "right";
};

export function ScheduleEventCard({
  item,
  index,
  align,
}: ScheduleEventCardProps) {
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const locationLabel =
    locale === "fr" ? "Lieu" : locale === "en" ? "Location" : "Ort";
  const openMapLabel =
    locale === "fr" ? "Ouvrir la carte" : locale === "en" ? "Open map" : "Karte oeffnen";
  const hasDirectLocationLink = /^https?:\/\//i.test(item.location.address.trim());
  const locationQuery = [item.location.name, item.location.address].filter(Boolean).join(", ");

  return (
    <motion.article
      initial={
        prefersReducedMotion
          ? false
          : { opacity: 0, x: align === "left" ? -28 : 28, y: 24 }
      }
      whileInView={prefersReducedMotion ? undefined : { opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={
        prefersReducedMotion
          ? undefined
          : { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: index * 0.06 }
      }
      className={styles.card}
    >
      <div className={styles.media}>
        <Image
          src={item.image}
          alt={item.title}
          fill
          sizes="(max-width: 959px) 100vw, 42vw"
          className={styles.image}
        />

        <div className={styles.imageOverlay} />

        <span className={styles.timeBadge}>{item.time}</span>
      </div>

      <div className={styles.cardBody}>
        {item.date ? (
          <div className={styles.dateBadge}>
            <CalendarDays size={16} aria-hidden="true" />
            <span>{formatDate(item.date, locale)}</span>
          </div>
        ) : null}

        <div className={styles.copy}>
          <h3 className={styles.cardTitle}>{item.title}</h3>
          <p className={styles.description}>{item.description}</p>
        </div>

        <a
          href={getMapsUrl(hasDirectLocationLink ? item.location.address : locationQuery)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.locationCard}
        >
          <span className={styles.locationIcon}>
            <MapPin size={18} aria-hidden="true" />
          </span>

          <span className={styles.locationText}>
            <span className={styles.locationLabel}>{locationLabel}</span>
            <strong className={styles.locationName}>{item.location.name}</strong>
            {item.location.address ? (
              <span className={styles.locationAddress}>
                {hasDirectLocationLink ? openMapLabel : item.location.address}
              </span>
            ) : null}
          </span>

          <ArrowUpRight size={16} aria-hidden="true" className={styles.locationArrow} />
        </a>
      </div>
    </motion.article>
  );
}
